// import { Button, Dialog, Message } from '@alifd/next';
import { Box, Typography } from '@mui/material';
import type { InputProps, InputRef } from 'antd';
import { Button, Input, message, Modal, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDragListView from 'react-drag-listview';
import Swal from 'sweetalert2';

import useService from '@/lib/hooks/useService';

import type { CategoryMenu } from '../../../EditService/types';
import type EditCategoriesProps from './types';

const EditCategories: React.FC<EditCategoriesProps> = ({
  categoryName,
  buttonLabel,
  modalTitle,
  onUpdate,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [categoryDataType, setCategoryDataType] = useState<'object' | 'string'>(
    'string'
  );
  const [categoryData, setCategoryData] = useState<CategoryMenu>();
  const [dataSource, setDataSource] = useState<
    { id: string; key: string; name: string }[]
  >([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>();

  const [resData, updateCategoryData, categoryMenuClient] =
    useService<CategoryMenu>({
      collectionName: 'categoryMenu',
      query: {
        name: categoryName,
      },
    });
  const [addInput, setAddInput] = useState<{
    value: string | undefined;
    status: InputProps['status'];
    errorMessage?: string;
  }>({
    value: undefined,
    status: '',
  });
  const [editInput, setEditInput] = useState<typeof addInput>({
    value: undefined,
    status: '',
  });

  const editInputRef = useRef<InputRef>(null);

  const validateCategory = useCallback(
    (inputValue: string) => {
      const nonSelectedCategories = dataSource.filter(
        (category) => category.id !== selectedCategoryId
      );
      if (
        nonSelectedCategories.find((category) => category.name === inputValue)
      ) {
        return false;
      }
      return true;
    },
    [dataSource, selectedCategoryId]
  );

  const changeData = useCallback(
    async (data: { id: string; name: string }[], manipulateText: string) => {
      let newData: CategoryMenu['data'];
      if (categoryDataType === 'string') {
        newData = data.map((category) => category.name);
      } else {
        newData = data.map((category) => ({
          id: category.id,
          name: category.name,
        }));
      }
      try {
        await categoryMenuClient.patch(categoryData?._id, {
          data: newData,
        });
        message.success(`${manipulateText}成功`);
        onUpdate?.();
        updateCategoryData();
        setEditInput({
          value: undefined,
          status: '',
        });
        setAddInput({
          value: undefined,
          status: '',
        });
        setSelectedCategoryId(undefined);
      } catch (error) {
        console.error(error);
        message.error(`${manipulateText}失敗`);
      }
    },
    [
      categoryData?._id,
      categoryDataType,
      categoryMenuClient,
      onUpdate,
      updateCategoryData,
    ]
  );

  const handleAddCategory = useCallback(async () => {
    if (addInput.value) {
      const newCategory = {
        id: moment().valueOf().toString(),
        name: addInput.value,
      };
      const newData = [...dataSource, newCategory];
      await changeData(newData, '新增');
    }
  }, [addInput.value, changeData, dataSource]);

  const handleEditCategory = useCallback(async () => {
    if (selectedCategoryId) {
      const newData = dataSource.map((category) => {
        if (category.id === selectedCategoryId && editInput.value) {
          return {
            ...category,
            name: editInput.value,
          };
        }
        return category;
      });
      await changeData(newData, '編輯');
    }
  }, [changeData, dataSource, editInput.value, selectedCategoryId]);

  const handleDeleteCategory = useCallback(
    async (id: string) => {
      const result = await Swal.fire({
        title: '確定要刪除此分類嗎？',
        text: '此動作無法復原',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      });
      if (result.isConfirmed) {
        const newData = dataSource.filter((category) => category.id !== id);
        await changeData(newData, '刪除');
      }
    },
    [changeData, dataSource]
  );

  const handleDragEnd = useCallback(
    async (fromIndex: number, toIndex: number) => {
      const newData = [...dataSource];
      const [removed] = newData.splice(fromIndex - 1, 1);
      newData.splice(toIndex - 1, 0, removed);
      await changeData(newData, '排序');
    },
    [changeData, dataSource]
  );

  const handleEditInputChange: InputProps['onChange'] = useCallback(
    (e) => {
      const value = e.target.value;
      if (value) {
        const valid = validateCategory(value);
        if (valid) {
          setEditInput({
            value,
            status: '',
          });
        } else {
          setEditInput({
            value,
            status: 'error',
            errorMessage: '此類別已存在',
          });
        }
      } else {
        setEditInput({
          value,
          status: 'error',
          errorMessage: '請輸入類別名稱',
        });
      }
    },
    [validateCategory]
  );

  const handleClose = useCallback(() => {
    setOpenModal(false);
    setAddInput({
      value: undefined,
      status: '',
    });
    setEditInput({
      value: undefined,
      status: '',
    });
    setSelectedCategoryId(undefined);
  }, []);

  useEffect(() => {
    if (editInputRef.current && selectedCategoryId) {
      editInputRef.current.focus();
    }
  }, [editInputRef, selectedCategoryId]);

  useEffect(() => {
    if (resData.length > 0) {
      setCategoryData(resData[0]);
      const newData = resData[0].data;
      const dataType = typeof newData[0];
      if (dataType === 'object' || dataType === 'string') {
        setCategoryDataType(dataType);
        setDataSource(() => {
          return newData.map((data: CategoryMenu['data'][0], index: number) => {
            if (typeof data === 'string') {
              return {
                id: index.toString(),
                key: index.toString(),
                name: data,
              };
            }
            return { ...data, key: index.toString() };
          });
        });
      }
    }
  }, [resData]);

  useEffect(() => {
    if (editInput.status === 'error' && editInput.errorMessage) {
      message.error(editInput.errorMessage);
    }
  }, [editInput.status, editInput.errorMessage]);

  useEffect(() => {
    if (addInput.status === 'error' && addInput.errorMessage) {
      message.error(addInput.errorMessage);
    }
  }, [addInput.status, addInput.errorMessage]);

  const columns: ColumnsType<typeof dataSource[0]> = useMemo(
    () => [
      {
        title: '類別名稱',
        dataIndex: 'name',
        key: 'name',
        width: '200px',
        render: (text, record) => {
          if (record.id === selectedCategoryId) {
            return (
              <Input
                value={editInput.value}
                status={editInput.status}
                onChange={handleEditInputChange}
                ref={editInputRef}
              />
            );
          }
          return text;
        },
      },
      {
        title: '操作',
        key: 'action',
        width: '200px',
        render: (_, record) => {
          return (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-around',
              }}
            >
              {record.id === selectedCategoryId && (
                <>
                  <Button
                    type="primary"
                    disabled={editInput.status === 'error'}
                    onClick={handleEditCategory}
                  >
                    確定
                  </Button>
                  <Button onClick={() => setSelectedCategoryId(undefined)}>
                    取消
                  </Button>
                </>
              )}
              {record.id !== selectedCategoryId && (
                <>
                  <Button
                    onClick={() => {
                      setEditInput({
                        value: record.name,
                        status: '',
                      });
                      setAddInput({
                        value: undefined,
                        status: '',
                      });
                      setSelectedCategoryId(record.id);
                    }}
                  >
                    編輯
                  </Button>
                  <Button
                    type="primary"
                    danger
                    onClick={() => handleDeleteCategory(record.id)}
                  >
                    刪除
                  </Button>
                </>
              )}
            </Box>
          );
        },
      },
    ],
    [
      editInput.status,
      editInput.value,
      handleDeleteCategory,
      handleEditCategory,
      handleEditInputChange,
      selectedCategoryId,
    ]
  );

  return (
    <>
      <Button onClick={() => setOpenModal(true)}>{buttonLabel}</Button>
      <Modal
        title={modalTitle}
        open={openModal}
        onCancel={handleClose}
        cancelText="關閉"
        footer={null}
      >
        <Box
          sx={{
            // width: '450px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
            }}
          >
            <Input
              placeholder="新增類別"
              status={addInput.status}
              value={addInput.value}
              onChange={(e) => {
                const value = e.target.value;
                const valid = validateCategory(value);
                if (valid) {
                  setAddInput({
                    value,
                    status: '',
                  });
                } else {
                  setAddInput({
                    value,
                    status: 'error',
                    errorMessage: '此類別已存在',
                  });
                }
              }}
              onFocus={() => setSelectedCategoryId(undefined)}
            />
            <Button
              disabled={addInput.status === 'error' || !addInput.value}
              onClick={handleAddCategory}
            >
              新增
            </Button>
          </Box>
          <Typography variant="subtitle2">拖曳項目以調整顯示順序</Typography>
          <ReactDragListView onDragEnd={handleDragEnd}>
            <Table
              bordered
              size="small"
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              scroll={{ y: 400, scrollToFirstRowOnChange: true }}
            />
          </ReactDragListView>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(EditCategories);
